/**
 * Centralized time-slot classification + SLOT_CONFIG single-source-of-truth.
 * Post VM/NM split (2026-06-15): canonical slots are VM (07:00–08:30 pickup,
 * dropoff ab 11:00), NM (14:00–15:30, ab 18:00), Saturday (10:30–14:30 tour).
 * Winterplan ab 2026-11-09 (WINTER_2026_DATE): NM 13:30–15:00, ab 17:30.
 * See SLOT_CONFIG below. slotToInternalId throws on legacy/unknown values.
 */

import { WANDERTAG_DATES, WANDERTAG_VM_ONLY_ALLOWED_DATES } from "../../supabase/functions/_shared/gassi-kapazitaet.ts";
import { berlinTagIso, WINTER_2026_DATE } from "../../supabase/functions/_shared/winter-2026.ts";

/** Saturday bookings are allowed starting from this date (inclusive) */
export const SATURDAY_START_DATE = new Date(2026, 2, 7); // March 7, 2026

/**
 * Wandertag dates: full-day hiking events where only ganztag (both morning + afternoon) bookings are allowed.
 * Q-D1 (27.10.2026): Termine stehen nur noch in supabase/functions/_shared/gassi-kapazitaet.ts,
 * dieselbe Datei prüft book-with-credit. Neue Wandertage dort eintragen.
 */
export { WANDERTAG_DATES, WANDERTAG_VM_ONLY_ALLOWED_DATES };

/** Check if a date string (YYYY-MM-DD) is a Wandertag */
export function isWandertagDate(dateStr: string): boolean {
  return WANDERTAG_DATES.includes(dateStr);
}

/**
 * Wandertag-Ausnahmen (WANDERTAG_VM_ONLY_ALLOWED_DATES, oben re-exportiert): Termine, an denen
 * zusätzlich zu Ganztag auch reine Vormittags-Buchungen erlaubt sind (Nachmittag bleibt an
 * Ganztag gekoppelt). Kapazitätsdeckel wird in der Edge Function `book-with-credit` durchgesetzt.
 */
export function isWandertagVmOnlyAllowed(dateStr: string): boolean {
  return WANDERTAG_VM_ONLY_ALLOWED_DATES.includes(dateStr);
}

/** Check if a given date is eligible for Saturday booking */
export function isSaturdayBookable(date: Date): boolean {
  return date.getDay() === 6 && date >= SATURDAY_START_DATE;
}

// ---- SLOT_CONFIG: single source of truth for customer-facing slot times ----
// VM/NM Pool-Split hard-switch 2026-06-15. All hardcoded slot times in the
// frontend MUST read from here — no second place-of-truth.
export type InternalSlotId = 'vm' | 'nm' | 'saturday';

export interface SlotConfig {
  label: string;            // "Vormittag"
  shortLabel: string;       // "VM"
  pickupWindow: string;     // "07:00-08:30" (customer display)
  pickupStart: string;      // "07:00"
  pickupEnd: string;        // "08:30"
  dropoffEarliest: string;  // "11:00" - Dropoff "ab" (hold-to-window)
  dropoffDisplay: string;   // booking-card dropoff text: "ab 11:00" (new) / "11:30-12:30" (old range)
  customerStringFull: string; // "Pickup 07:00-08:30, Dropoff ab 11:00"
}

function buildSlot(
  cfg: Omit<SlotConfig, 'customerStringFull' | 'dropoffDisplay'> & { customerStringFull?: string; dropoffDisplay?: string },
): SlotConfig {
  return {
    ...cfg,
    dropoffDisplay: cfg.dropoffDisplay ?? `ab ${cfg.dropoffEarliest}`,
    customerStringFull:
      cfg.customerStringFull ?? `Pickup ${cfg.pickupWindow}, Dropoff ab ${cfg.dropoffEarliest}`,
  };
}

export const SLOT_CONFIG: Record<InternalSlotId, SlotConfig> = {
  vm: buildSlot({ label: 'Vormittag', shortLabel: 'VM', pickupWindow: '07:00-08:30', pickupStart: '07:00', pickupEnd: '08:30', dropoffEarliest: '11:00' }),
  nm: buildSlot({ label: 'Nachmittag', shortLabel: 'NM', pickupWindow: '14:00-15:30', pickupStart: '14:00', pickupEnd: '15:30', dropoffEarliest: '18:00' }),
  // Samstag-Rückgabe: Routenplanung legt die erste Rückgabe auf 13:30, Tour-Ende hart 14:30
  // (charlies-team SATURDAY_FIRST_DROPOFF_ANCHOR_MIN). dropoffEarliest bleibt für Bestandsnutzer.
  saturday: buildSlot({ label: 'Samstag', shortLabel: 'Sa', pickupWindow: '09:00-10:20', pickupStart: '09:00', pickupEnd: '10:20', dropoffEarliest: '14:30', dropoffDisplay: 'ca. 13:30-14:30', customerStringFull: 'Samstag-Tour 09:00-14:30' }),
};

// ============================================================================
// TWO WORLDS DATE-GATE (pre-cutover)
// ----------------------------------------------------------------------------
// Founder decision (Spec 2026-06-03): bookings for dates >= VM_NM_SPLIT_DATE must
// show + write the NEW VM/NM times IMMEDIATELY (before the routing cutover), while
// dates <= 2026-06-13 keep the EXACT old behavior. Saturday is unchanged in both.
// (2026-06-14 is a Saturday → Saturday logic.)
//
// SLOT_CONFIG above = the NEW world (>= 2026-06-15). OLD_SLOT_CONFIG below = the
// pre-Block-B world (<= 2026-06-13), recovered byte-for-byte from commit 8074775
// (last pre-Block-B commit). The ONLY real difference is NM + dropoff comms; VM
// pickup is effectively unchanged. Both must coexist — pick via slotConfigForDate.
// ============================================================================

/** First date (inclusive) on which the NEW VM/NM slot times apply in the booking flow. */
export const VM_NM_SPLIT_DATE = '2026-06-15';

/** OLD slot config (<= 2026-06-13). Recovered from commit 8074775 (pre-Block-B). */
export const OLD_SLOT_CONFIG: Record<InternalSlotId, SlotConfig> = {
  // OLD VM: pickup 07:00–08:30, dropoff 11:30–12:30 (range, not "ab")
  vm: buildSlot({ label: 'Vormittag', shortLabel: 'VM', pickupWindow: '07:00-08:30', pickupStart: '07:00', pickupEnd: '08:30', dropoffEarliest: '11:30', dropoffDisplay: '11:30-12:30', customerStringFull: 'Pickup 07:00-08:30, Dropoff 11:30-12:30' }),
  // OLD NM: pickup 12:30–14:00, dropoff 16:30–18:00 (single window, no 14:00 split)
  nm: buildSlot({ label: 'Nachmittag', shortLabel: 'NM', pickupWindow: '12:30-14:00', pickupStart: '12:30', pickupEnd: '14:00', dropoffEarliest: '16:30', dropoffDisplay: '16:30-18:00', customerStringFull: 'Pickup 12:30-14:00, Dropoff 16:30-18:00' }),
  // Saturday unchanged in both worlds
  saturday: buildSlot({ label: 'Samstag', shortLabel: 'Sa', pickupWindow: '10:30-11:00', pickupStart: '10:30', pickupEnd: '11:00', dropoffEarliest: '14:30', customerStringFull: 'Samstag-Tour 10:30-14:30' }),
};

// ============================================================================
// DRITTE WELT: WINTERPLAN AB MO 09.11.2026 (Variante B, Noah 19.10.2026)
// ----------------------------------------------------------------------------
// Ab WINTER_2026_DATE: NM-Abholung 13:30–15:00 (früh 13:30–14:15 / spät
// 14:15–15:00), NM-Rückgabe ab 17:30. VM unverändert (Abholung 07:00–08:30,
// Rückgabe ab 11:00). Samstag unverändert. Gespeichert wird weiter das Wort
// („Nachmittags“), der Wechsel betrifft nur die angezeigten Zeiten.
// Spiegel: charlies-team supabase/functions/_shared/winter-2026.ts und
// supabase/functions/_shared/gassi-mail.ts (abholRueckgabe).
// ============================================================================

/** Erster Tag (inklusive) des Winterplans. Stichtag steht nur in supabase/functions/_shared/winter-2026.ts. */
export { WINTER_2026_DATE };

/** Winter-Konfiguration (>= 2026-11-09). */
export const WINTER_SLOT_CONFIG: Record<InternalSlotId, SlotConfig> = {
  vm: SLOT_CONFIG.vm,
  nm: buildSlot({ label: 'Nachmittag', shortLabel: 'NM', pickupWindow: '13:30-15:00', pickupStart: '13:30', pickupEnd: '15:00', dropoffEarliest: '17:30' }),
  saturday: SLOT_CONFIG.saturday,
};

/** Which "world" a given date falls into. */
export type SlotWorld = 'old' | 'new' | 'winter';

/**
 * Resolve the world for a date string (YYYY-MM-DD).
 * Saturday is handled by the slot itself (saturday config is identical in all
 * worlds), so this only distinguishes old vs new vs winter for VM/NM.
 *   date >= 2026-11-09 → 'winter'
 *   date >= 2026-06-15 → 'new'
 *   date <= 2026-06-13 → 'old'  (2026-06-14 is a Saturday)
 * Ohne Datum: 'new' (Rückwärtskompatibilität, wie bisher).
 */
export function slotWorldForDate(dateISO: string | null | undefined): SlotWorld {
  if (!dateISO) return 'new';
  // String compare is safe + timezone-proof for canonical YYYY-MM-DD.
  const d = dateISO.slice(0, 10);
  if (d >= WINTER_2026_DATE) return 'winter';
  return d >= VM_NM_SPLIT_DATE ? 'new' : 'old';
}

/** The full SlotConfig record (vm/nm/saturday) appropriate for a given date. */
export function slotConfigForDate(dateISO: string | null | undefined): Record<InternalSlotId, SlotConfig> {
  const world = slotWorldForDate(dateISO);
  if (world === 'winter') return WINTER_SLOT_CONFIG;
  return world === 'new' ? SLOT_CONFIG : OLD_SLOT_CONFIG;
}

/**
 * SlotConfig für den heutigen Berliner Tag (Website-/SEO-Texte ohne Buchungsdatum).
 * Ab WINTER_2026_DATE die Winterzeiten, vorher die bisherigen. Beim Build (llms.txt) gilt
 * der Build-Tag, im Browser der Tag des Seitenaufrufs.
 */
export function slotConfigHeute(jetzt: Date = new Date()): Record<InternalSlotId, SlotConfig> {
  return slotConfigForDate(berlinTagIso(jetzt));
}

/** Customer pickup window string ("07:00–08:30") for a slot on a given date. */
export function pickupWindowForDate(input: string, dateISO: string | null | undefined): string {
  return slotConfigForDate(dateISO)[slotToInternalId(input)].pickupWindow;
}

// VM/NM hard-switch (2026-06-15): only the new canonical pickup times are recognized.
// Legacy values (09:xx, 11:xx, 12:30, 13:xx, 16:xx, 17:xx, afternoon_early/late) are
// INTENTIONALLY not matched — slotToInternalId throws on them rather than guessing.
// This is by design: the cross-repo sync (sync-to-route-planner) must fail loudly on
// un-migrated data instead of silently mapping it. Block C migrates such rows
// server-side before this code goes live. Do NOT add legacy patterns here.
// Winterplan 09.11.2026: '13:30' und '14:15' ergänzt (NM-Fenster 13:30–15:00 und
// Unterfenster 13:30–14:15 / 14:15–15:00), damit slotToInternalId nicht wirft.
const VM_PATTERNS = ['vormittag', 'vm', 'morning', '07:00', '07:30', '08:00', '08:30'];
const NM_PATTERNS = ['nachmittag', 'nm', 'afternoon', '13:30', '14:00', '14:15', '14:30', '15:00', '15:30'];
const SATURDAY_PATTERNS = ['samstag', 'saturday', '10:30'];

export function slotToInternalId(input: string): InternalSlotId {
  const s = input.toLowerCase().trim();
  if (isSaturdaySlot(input) || SATURDAY_PATTERNS.some(p => s.includes(p))) return 'saturday';
  if (VM_PATTERNS.some(p => s.includes(p))) return 'vm';
  if (NM_PATTERNS.some(p => s.includes(p))) return 'nm';
  throw new Error(`Cannot normalize slot input: "${input}"`);
}

export function formatSlotPickupWindow(input: string): string {
  return `Pickup ${SLOT_CONFIG[slotToInternalId(input)].pickupWindow}`;
}

export function formatSlotDropoffWindow(input: string): string {
  return `Dropoff ab ${SLOT_CONFIG[slotToInternalId(input)].dropoffEarliest}`;
}

export function isMorningSlot(slot: string | null | undefined): boolean {
  if (!slot) return false;
  const s = slot.toLowerCase().trim();
  return VM_PATTERNS.some(p => s.includes(p)) && !isSaturdaySlot(slot);
}

export function isAfternoonSlot(slot: string | null | undefined): boolean {
  if (!slot) return false;
  const s = slot.toLowerCase().trim();
  return NM_PATTERNS.some(p => s.includes(p)) && !isSaturdaySlot(slot);
}

export function isSaturdaySlot(slot: string | null | undefined): boolean {
  if (!slot) return false;
  const s = slot.toLowerCase().trim();
  
  return (
    s === "saturday" ||
    s.startsWith("samstag") ||
    s === "10:30 - 14:30 uhr" ||
    s === "10:30-14:30" ||
    s.includes("10:30 - 14:30") ||
    s.includes("10:30-14:30")
  );
}

export function formatTimeSlotLabel(slot: string | null | undefined): string {
  if (!slot) return "";
  if (isSaturdaySlot(slot)) return "Samstag";
  if (isMorningSlot(slot)) return "Vormittags";
  if (isAfternoonSlot(slot)) return "Nachmittags";
  return slot;
}

/**
 * Operational route/shift window per slot for admin + walker views (NOT the customer
 * pickup window — that's SLOT_CONFIG). VM stays 08:00–12:00 (routes unchanged); NM moved
 * to 14:00–18:00 with the VM/NM split; Saturday 10:30–14:30.
 *
 * Date-gate: pass the booking's date (YYYY-MM-DD) to honor the two-worlds rule —
 * a <= 2026-06-13 NM booking still shows the old 13:00-17:00 operational window.
 * When dateISO is omitted, the NEW world is used (back-compat default).
 */
export function formatTimeSlotRange(slot: string | null | undefined, dateISO?: string | null): string {
  if (!slot) return "";
  if (isSaturdaySlot(slot)) return "09:00-14:30 Uhr";
  if (isMorningSlot(slot)) return "08:00-12:00 Uhr";
  if (isAfternoonSlot(slot)) {
    const world = slotWorldForDate(dateISO ?? VM_NM_SPLIT_DATE);
    // Winterplan ab 09.11.2026: NM 30 Minuten früher (Abholung ab 13:30, Rückgabe ab 17:30).
    if (world === 'winter') return "13:30-17:30 Uhr";
    return world === 'new' ? "14:00-18:00 Uhr" : "13:00-17:00 Uhr";
  }
  return slot;
}

/**
 * Ops-Fenster (Route/Schicht) ohne „Uhr“, z. B. "13:30-17:30", für Admin-Labels ohne Buchung.
 * Ohne Datum gilt der heutige Berliner Tag (ab WINTER_2026_DATE also das Winterfenster).
 */
export function opsFensterFuerTag(slot: 'vm' | 'nm', dateISO?: string | null): string {
  return formatTimeSlotRange(slot === 'vm' ? 'vormittag' : 'nachmittag', dateISO || berlinTagIso()).replace(/ Uhr$/, '');
}

// Single source for pickup-preference sub-windows (customer "früh/spät" choice within a slot).
// VM split 07:00–07:40 / 07:40–08:30; NM split 14:00–14:45 / 14:45–15:30 (VM/NM Pool-Split).
export const PICKUP_WINDOW_LABELS: Record<string, string> = {
  early: '07:00-07:40',
  late: '07:40-08:30',
  afternoon_early: '14:00-14:45',
  afternoon_late: '14:45-15:30',
  saturday_early: '09:00-09:40',
  saturday_late: '09:30-10:20',
};

/**
 * OLD pickup sub-window labels (<= 2026-06-13). Recovered from commit 8074775.
 * VM (early/late) is identical to the new world; NM (afternoon_early/late) differs:
 * OLD afternoon_early = 11:30–12:30, afternoon_late = 12:30–13:30.
 * NOTE: the persisted string ('afternoon_early'/'afternoon_late') is IDENTICAL in
 * both worlds — only its human-readable time interpretation is date-dependent.
 */
export const OLD_PICKUP_WINDOW_LABELS: Record<string, string> = {
  early: '07:00-07:40',
  late: '07:40-08:30',
  afternoon_early: '11:30-12:30',
  afternoon_late: '12:30-13:30',
  saturday_early: '09:00-09:40',
  saturday_late: '09:30-10:20',
};

/** Unterfenster im Winterplan (>= 2026-11-09): nur NM verschiebt sich um 30 Minuten. */
export const WINTER_PICKUP_WINDOW_LABELS: Record<string, string> = {
  ...PICKUP_WINDOW_LABELS,
  afternoon_early: '13:30-14:15',
  afternoon_late: '14:15-15:00',
};

/** The pickup-window label map (sub-windows) appropriate for a given date. */
export function pickupWindowLabelsForDate(dateISO: string | null | undefined): Record<string, string> {
  const world = slotWorldForDate(dateISO);
  if (world === 'winter') return WINTER_PICKUP_WINDOW_LABELS;
  return world === 'new' ? PICKUP_WINDOW_LABELS : OLD_PICKUP_WINDOW_LABELS;
}

export function formatPickupWindow(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value === 'no_preference') return 'Keine Präferenz';
  return PICKUP_WINDOW_LABELS[value] ?? value;
}

/**
 * Date-aware variant of formatPickupWindow: interprets the (date-agnostic) stored
 * sub-window string against the world the booking's date falls into. Use this for
 * display of EXISTING bookings so a <= 2026-06-13 NM booking still shows its old
 * 11:30–12:30 / 12:30–13:30 window instead of the new 14:00 split.
 */
export function formatPickupWindowForDate(
  value: string | null | undefined,
  dateISO: string | null | undefined,
): string | null {
  if (!value) return null;
  if (value === 'no_preference') return 'Keine Präferenz';
  return pickupWindowLabelsForDate(dateISO)[value] ?? value;
}

/** Welche Präferenz-Werte zu welchem Slot gehören — eine fremde Präferenz (Altdaten) fällt aufs volle Fenster zurück. */
const PICKUP_WINDOW_PREFIX: Record<InternalSlotId, (v: string) => boolean> = {
  vm: (v) => v === 'early' || v === 'late',
  nm: (v) => v.startsWith('afternoon_'),
  saturday: (v) => v.startsWith('saturday_'),
};

/**
 * Abholfenster + Rückgabezeit einer Buchung für die Kundenanzeige, z. B.
 * { abholung: "07:40-08:30 Uhr", rueckgabe: "ab 11:00 Uhr" }. Gewähltes Unterfenster
 * (preferred_pickup_window) schlägt das volle Slot-Fenster. null bei Ganztag/unbekannt.
 * Gespiegelt in supabase/functions/_shared/gassi-mail.ts (abholRueckgabe) — beide gleich halten.
 */
export function abholRueckgabeForBooking(
  timeSlot: string | null | undefined,
  preferredPickupWindow: string | null | undefined,
  dateISO: string | null | undefined,
): { abholung: string; rueckgabe: string } | null {
  if (!timeSlot) return null;
  let id: InternalSlotId;
  try {
    id = slotToInternalId(timeSlot);
  } catch {
    return null;
  }
  const cfg = slotConfigForDate(dateISO)[id];
  const pref = preferredPickupWindow ?? '';
  const unterfenster = PICKUP_WINDOW_PREFIX[id](pref) ? pickupWindowLabelsForDate(dateISO)[pref] : undefined;
  return {
    abholung: `${unterfenster ?? cfg.pickupWindow} Uhr`,
    rueckgabe: `${cfg.dropoffDisplay} Uhr`,
  };
}

// Warteliste-Frist: entfällt seit 08.10.2026 (Noah), die Warteliste schließt nie. Nach der
// Gassi-Buchungsfrist landet jede Buchung auf der Warteliste (src/lib/gassiBuchungsfrist.ts).
